import React from "react";

// "The Founding Partner" pricing section from the launch-package page. Shared with the
// business page; `onApply` decides what "Apply Now" does on each page.

export type TierValue = "pioneer" | "growth" | "founding";

export const TIER_VALUES: TierValue[] = ["founding", "growth", "pioneer"];

// "featured" = red premium card, "standard" = pink card, "plain" = white card.
export type TierVariant = "featured" | "standard" | "plain";

export interface PricingTier<T extends string = TierValue> {
  value: T;
  title: string;
  price: string;
  period?: string;
  valueText: string;
  items: string[];
  variant: TierVariant;
  badgeText?: string;
  buttonText?: string;
}

const PRICING_TIERS: PricingTier[] = [
  {
    value: "founding",
    title: "Founding Partner",
    price: "$485",
    period: "/ 02-Years",
    valueText: "Over $2,000+ Value",
    items: [
      "2-Years Premium Membership",
      "$500 Marketing Credits",
      "200 Premium Membership Invites",
      "Elite Marketing Launch Kit",
      "First 10' Founding Badge*",
      "2 Featured Posts",
      "Priority Visibility",
      "Local Influencer Reel",
    ],
    variant: "featured",
  },
  {
    value: "growth",
    title: "Growth Partner",
    price: "$319",
    period: "/ 01-Year",
    valueText: "Over $1,000+ Value",
    items: [
      "1-Year Premium Membership",
      "$300 Marketing Credits",
      "100 Premium Membership Invites",
      "Pro Marketing Launch Kit",
      "First 10' Founding Badge*",
      "1 Featured Posts",
    ],
    variant: "standard",
  },
  {
    value: "pioneer",
    title: "Pioneer",
    price: "$99",
    period: "/ 03-Months",
    valueText: "Over $300+ Value",
    items: [
      "3-Months Premium Membership",
      "$100 Marketing Credits",
      "20 Premium Membership Invites",
      "Starter Marketing Launch Kit",
    ],
    variant: "plain",
  },
];

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center justify-center gap-3">
    <span className="w-[14px] h-[14px] rounded-full bg-[#D90000]" />
    <span className="text-[16px] min-[1442px]:text-[18px] leading-none font-medium text-[#2A2A2A]">{children}</span>
  </div>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const CrownIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 8l3.5 3L12 5l5.5 6L21 8l-2 10H5L3 8zm4.2 9h9.6l1.13-5.66-1.72 1.48a1 1 0 01-1.45-.15L12 8.32l-2.76 4.35a1 1 0 01-1.45.15l-1.72-1.48L7.2 17z" />
  </svg>
);

type FoundingPartnerSectionProps<T extends string> = {
  onApply: (tier: T) => void;
  tiers?: PricingTier<T>[];
  label?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  footnote?: React.ReactNode | null;
};

const DEFAULT_LABEL = "The Founding Partner";

const DEFAULT_TITLE = (
  <>
    Choose the partnership package that aligns with{" "}
    <span className="text-[#E00000] font-bold">your vision.</span>
  </>
);

const DEFAULT_SUBTITLE = "We have a launch package for every level of community engagement, starting at just $99.";

const DEFAULT_FOOTNOTE = (
  <>
    *Earn 500 Points ($5 value in Marketing Credits) every year for each invite activated. The
    "First 10" Badge is limited exclusively to the first 10 Premium members to complete onboarding
    in your city.
  </>
);

const CARD_BG: Partial<Record<TierVariant, string>> = {
  featured: "url('/images/launch/PremiumMembershipPackageCardBg.svg')",
  standard: "url('/images/launch/StandardMembershipPackageCardBg.svg')",
};

export function FoundingPartnerSection<T extends string = TierValue>({
  onApply,
  tiers = PRICING_TIERS as unknown as PricingTier<T>[],
  label = DEFAULT_LABEL,
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  footnote = DEFAULT_FOOTNOTE,
}: FoundingPartnerSectionProps<T>) {
  const maxRows = Math.max(...tiers.map((p) => p.items.length));

  return (
    <section className="w-full flex justify-center py-20 bg-[#F3F3F3]" id="pricing">
      <div className="w-full max-w-[1440px] px-6">
        <SectionLabel>{label}</SectionLabel>
        <h2 className="mt-5 text-center text-[#171717] text-[30px] sm:text-[40px] min-[1442px]:text-[38px] min-[1442px]:sm:text-[50px] leading-[1.15] font-medium tracking-[-1px] max-w-[760px] mx-auto">
          {title}
        </h2>
        <p className="mt-4 text-center text-[#2B2B2B] text-[15px] min-[1442px]:text-[17px] max-w-[620px] mx-auto">
          {subtitle}
        </p>

        <div
          className={`mt-14 grid grid-cols-1 gap-6 items-start px-0 sm:px-12 ${
            tiers.length === 2 ? "lg:grid-cols-2 max-w-[1000px] mx-auto" : "lg:grid-cols-3"
          }`}
        >
          {tiers.map((plan) => {
            const featured = plan.variant === "featured";
            const blanksNeeded = maxRows - plan.items.length;

            return (
              <div key={plan.value} className="flex flex-col">
                {/* Price card */}
                <div
                  className={`relative rounded-[28px] overflow-hidden shadow-lg bg-cover bg-center ${
                    featured
                      ? "text-white"
                      : plan.variant === "standard"
                        ? "text-[#171717]"
                        : "text-[#171717] border border-gray-200 bg-white"
                  }`}
                  style={CARD_BG[plan.variant] ? { backgroundImage: CARD_BG[plan.variant] } : undefined}
                >
                  {plan.badgeText && (
                    <span
                      className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[12px] font-semibold ${
                        featured ? "bg-white text-[#E00000]" : "bg-[#E00000] text-white"
                      }`}
                    >
                      {plan.badgeText}
                    </span>
                  )}
                  <div className="px-8 pt-7 pb-8 text-center">
                    <p
                      className={`flex items-center justify-center gap-2 text-[20px] min-[1442px]:text-[25px] font-bold ${
                        featured ? "text-[#F5C518]" : plan.variant === "standard" ? "text-[#E0325F]" : "text-[#171717]"
                      }`}
                    >
                      {featured && <CrownIcon className="w-5 h-5" />}
                      {plan.title}
                    </p>
                    <div className="mt-3 flex items-end justify-center gap-1">
                      <span
                        className={`text-[36px] min-[1442px]:text-[45px] leading-none font-black tracking-tight ${
                          featured ? "text-white" : "text-[#171717]"
                        }`}
                      >
                        {plan.price}
                      </span>
                      {plan.period && (
                        <span
                          className={`text-[15px] min-[1442px]:text-[17px] leading-none mb-[3px] ${
                            featured ? "text-white/80" : "text-gray-500"
                          }`}
                        >
                          {" "}
                          {plan.period}
                        </span>
                      )}
                    </div>
                    <div
                      className={`mt-6 pt-3 text-[13px] min-[1442px]:text-[15px] font-semibold border-t ${
                        featured
                          ? "border-white/25 text-white"
                          : plan.variant === "standard"
                            ? "border-black/10 text-[#171717]"
                            : "border-gray-200 text-gray-600"
                      }`}
                    >
                      {plan.valueText}
                    </div>
                  </div>
                </div>

                {/* Checklist card */}
                <div className="mt-6 rounded-[28px] bg-[#FDF2F2] border border-[#F5E3E3] px-8 py-8 flex-1">
                  <ul className="space-y-5">
                    {plan.items.map((item) => (
                      <li key={item} className="flex items-center gap-3">
                        <span className="w-[22px] h-[22px] min-[1442px]:w-[24px] min-[1442px]:h-[24px] rounded-full bg-[#E00000] text-white flex items-center justify-center shrink-0">
                          <CheckIcon className="w-3 h-3" />
                        </span>
                        <span className="text-[16px] min-[1442px]:text-[18px] text-[#171717]">{item}</span>
                      </li>
                    ))}
                    {Array.from({ length: blanksNeeded }).map((_, i) => (
                      <li key={`blank-${i}`} className="h-[22px] flex items-center">
                        <span className="w-full border-t border-gray-300" />
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Apply button */}
                <button
                  type="button"
                  onClick={() => onApply(plan.value)}
                  className="mt-6 inline-flex items-center justify-center h-[56px] px-8 min-[1442px]:h-[60px] min-[1442px]:px-9 rounded-xl text-[16px] min-[1442px]:text-[18px] font-bold no-underline bg-[#3c3c3c] border-0 text-white transition hover:bg-[#2B2B2B]"
                >
                  {plan.buttonText || "Apply Now"}
                </button>
              </div>
            );
          })}
        </div>

        {footnote && <p className="text-center text-xs text-gray-400 mt-10 max-w-2xl mx-auto">{footnote}</p>}
      </div>
    </section>
  );
}
